"use client";

import htmr from "htmr";
import { useMemo } from "react";

import { useContentResource } from "@ctv/core";
import { Text, View } from "@ctv/shared-core/src";

import { ALPHABETH, MAX_SECTION } from "../constants";
import { termsAndConditionsStyles as styles } from "../styles";
import { Section } from "../types";
import { constructNumberSection, constructRomanTitleSection } from "../utils";
import NumbersRenderer from "./NumbersRenderer";
import { RomanTitlesRenderer } from "./RomanTitlesRenderer";

const SECTION_ORDER = [
  "sectionDefinitions",
  "sectionPurposeAndObjective",
  "sectionScopes",
  "sectionCorporateBookingAccess",
  "sectionConfidentiality",
  "sectionRightsAndObligations",
  "sectionPaymentMethod",
  "sectionRefundAndReschedule",
  "sectionPriceAndPromotion",
  "sectionIntellectualPropertyRights",
  "sectionTaxAndOtherExpenses",
  "sectionPublication",
  "sectionNotification",
  "sectionRepresentationsAndWarranties",
  "sectionPersonalDataProtection",
  "sectionTermination",
  "sectionForceMajeure",
  "sectionGoverningLawAndDisputeSettlement",
  "sectionNonExclusivity",
  "sectionAddendum",
  "sectionMiscellaneous",
];

export default function SectionsRenderer() {
  const cr = useContentResource().CorporateTermsAndConditions;

  const sections = useMemo<Section[]>(() => {
    const sections: Section[] = [];
    for (let i = 0; i < MAX_SECTION; i++) {
      const sectionKey = SECTION_ORDER[i] as keyof typeof cr;
      const section: Section = {
        title: cr[sectionKey],
      };

      const bodyKey = sectionKey.replace("section", "body") as keyof typeof cr;
      section.body = cr[bodyKey];

      if (!sectionKey.includes("PaymentMethod")) {
        section.numberSections = constructNumberSection(sectionKey, cr);
      } else {
        section.romanTitleSections = constructRomanTitleSection(sectionKey, cr);
      }

      sections.push(section);
    }
    return sections;
  }, []);

  return (
    <>
      {sections.map((section, index) => (
        <View key={index} row>
          <Text style={styles.list} variant="title-3">
            {ALPHABETH[index]}.
          </Text>
          <View spacing="m" flex gapSpacing>
            <Text variant="title-3">{htmr(section.title)}</Text>
            {section.body ? (
              <Text style={styles.justify} variant="ui-small">
                {htmr(section.body)}
              </Text>
            ) : null}
            {section.numberSections ? (
              <NumbersRenderer numberSections={section.numberSections} />
            ) : (
              <RomanTitlesRenderer
                romanTitleSections={section.romanTitleSections}
              />
            )}
          </View>
        </View>
      ))}
    </>
  );
}
